import { RTLObserver } from "@ingka/webc-tools";
import { Speedtracker } from "../features/utils/speed-tracker.js";
import {
  NavigationEvent,
  NavigationEventDirection
} from "./CarouselControls.js";
const styles = ':host{--height:.125rem;align-items:center;cursor:pointer;display:flex;height:1.875rem;position:relative;touch-action:none;transition:opacity .1s cubic-bezier(.4,0,.4,1)}:host(.hide){opacity:0}:host(:hover){--height:.25rem}.track{align-items:center;background-color:rgb(var(--colour-neutral-3,223,223,223));display:flex;transition:height .1s cubic-bezier(.4,0,.4,1);width:100%}.handle,.track{border-radius:var(--height);height:var(--height)}.handle{background-color:rgb(var(--colour-neutral-7,17,17,17));margin-inline-start:var(--skapaScrollHandleLeft,0);position:relative;transition:inherit;transition-property:inherit;width:var(--skapaScrollHandleWidth,10%)}.handle.drag,.handle.drag:hover{--height:.5rem}.handle:after{content:" ";cursor:grab;height:2.75rem;left:50%;min-width:2.75rem;position:absolute;transform:translate(-50%,-50%);width:100%;z-index:3}';
import SkapaElement from "@ingka/skapa-webc-element";
function generateTemplate({ html }) {
  const template = document.createElement("template");
  template.innerHTML = html`
    <style>
      ${styles}
    </style>
    <div class="track" draggable="false">
      <div class="handle" draggable="false"></div>
    </div>
  `;
  return { main: template };
}
function cancelEvent(ev) {
  ev.preventDefault();
}
class Scrollbar extends SkapaElement {
  constructor() {
    super();
    this.dragOffsetX = 0;
    this.animationID = -1;
    this.speedTracker = new Speedtracker();
    this.handleDragStart = (ev) => {
      if (typeof window !== "undefined") {
        window.addEventListener("pointerleave", this.handleDragEnd);
        window.addEventListener("pointermove", this.updatePosition);
        window.addEventListener("pointerup", this.handleDragEnd);
      }
      this.handle.classList.toggle("drag", true);
      this.speedTracker.value = this.currentOffset;
      const biDirectionalOffset = this.isRTL ? this.handle.offsetLeft + this.handle.clientWidth - this.clientWidth : this.handle.offsetLeft;
      this.dragOffsetX = ev.pageX - biDirectionalOffset;
      this.dispatchEvent(new CustomEvent("skapa-scrollbar-scroll-start"));
    };
    this.handleDragEnd = () => {
      cancelAnimationFrame(this.animationID);
      if (typeof window !== "undefined") {
        window.removeEventListener("pointerleave", this.handleDragEnd);
        window.removeEventListener("pointerup", this.handleDragEnd);
        window.removeEventListener("pointermove", this.updatePosition);
      }
      this.handle.classList.toggle("drag", false);
      this.handle.style.setProperty("--skapaScrollHandleLeft", null);
      this.dispatchEvent(
        new CustomEvent("skapa-scrollbar-scroll-end", {
          detail: this.speedTracker.speed * 100
        })
      );
    };
    this.updatePosition = (ev) => {
      const rawDragOffset = (ev.pageX - this.dragOffsetX) * (this.isRTL ? -1 : 1);
      const dragPos = Math.min(
        Math.max(rawDragOffset, 0),
        this.clientWidth - this.handle.clientWidth
      ) / this.clientWidth;
      cancelAnimationFrame(this.animationID);
      this.animationID = requestAnimationFrame(() => {
        this.speedTracker.value = this.currentOffset;
        this.handle.style.setProperty(
          "--skapaScrollHandleLeft",
          `${(100 * dragPos).toFixed(2)}%`
        );
        this.dispatchEvent(
          new CustomEvent("skapa-scrollbar-scroll", { detail: { dragPos } })
        );
      });
    };
    this.navigateTrack = (ev) => {
      if (ev.composedPath()[0] === this.handle)
        return;
      const scrollBarPosition = this.getBoundingClientRect();
      const clickTarget = (ev.pageX - scrollBarPosition.x) / scrollBarPosition.width;
      const scrollBarCenter = this.handle.clientWidth * 0.5 / scrollBarPosition.width;
      const targetOffsetPercent = Math.max(
        Math.min(
          (this.isRTL ? 1 - clickTarget : clickTarget) - scrollBarCenter,
          1
        ),
        0
      );
      this.dispatchEvent(
        new CustomEvent("skapa-scrollbar-scroll", {
          detail: { dragPos: targetOffsetPercent.toFixed(2) }
        })
      );
    };
    this.handleKey = (ev) => {
      if (ev.shiftKey || ev.ctrlKey || ev.metaKey || ev.altKey)
        return;
      const forwardKey = this.isRTL ? "ArrowLeft" : "ArrowRight";
      const backwardKey = this.isRTL ? "ArrowRight" : "ArrowLeft";
      const { PREV, NEXT } = NavigationEventDirection;
      if (ev.key === backwardKey) {
        ev.preventDefault();
        this.dispatchEvent(new NavigationEvent(PREV));
      }
      if (ev.key === forwardKey) {
        ev.preventDefault();
        this.dispatchEvent(new NavigationEvent(NEXT));
      }
    };
    const shadow = this.attachShadow({ mode: "open" });
    const template = this.getTemplates(generateTemplate);
    shadow.append(template.main.content.cloneNode(true));
    this.handle = shadow.querySelector(".handle");
  }
  get isRTL() {
    return this.hasAttribute("skapa-rtl");
  }
  /** Returns the current scroll offset value in decimal percentage; 0 < value < 1  */
  get currentOffset() {
    return this.handle.offsetLeft / (this.clientWidth - this.handle.clientWidth);
  }
  connectedCallback() {
    RTLObserver.addTarget(this);
    this.setAttribute("aria-hidden", "true");
    this.handle.addEventListener("pointerdown", this.handleDragStart);
    this.addEventListener("click", this.navigateTrack);
    this.addEventListener("dragstart", cancelEvent);
    this.addEventListener("keydown", this.handleKey);
  }
  disconnectedCallback() {
    RTLObserver.removeTarget(this);
    this.handle.removeEventListener("pointerdown", this.handleDragStart);
    this.handleDragEnd();
    this.removeEventListener("click", this.navigateTrack);
    this.removeEventListener("dragstart", cancelEvent);
    this.removeEventListener("keydown", this.handleKey);
  }
}
Scrollbar.skapaTagName = "carousel-scrollbar";
Scrollbar.skapaDependencies = [];
Scrollbar.observedAttributes = ["skapa-rtl"];
export {
  Scrollbar
};
